import type { ApiProduct } from '@/types/api-product'
import type { CartItem } from '@/stores/cart'
import { getImageUrl } from '@/utils/imageUrl'

/**
 * Obtiene una imagen mockeada basada en el nombre y categoría del producto
 */
function getMockImage(apiProduct: ApiProduct): string {
  // Si el producto tiene imágenes de la API, usarlas primero (normalizar por si vienen con localhost)
  if (apiProduct.images && apiProduct.images.length > 0 && apiProduct.images[0].Image_url) {
    return getImageUrl(apiProduct.images[0].Image_url)
  }

  // Lista de imágenes disponibles
  const availableImages = [
    '/logos/isolateprotein-coldbrew.webp',
    '/logos/Creatine_30_serv_render.webp',
    '/logos/essential_pre-_champion_mentality.webp',
    '/logos/eaaplus-pineapple.webp',
    '/logos/REPLENISH_LEMON_LIME_FRONT.webp',
    '/logos/FUEL-Orange-Front_48a9211d-cc14-489a-8ebf-13bf3af02482.webp',
    '/logos/ACETYL_L_CARN_NEW_FRONT_b06488b7-fc9d-4baf-b68e-191985ac0132.webp',
    '/logos/itholate-championmentality.webp',
    '/logos/thavage_-_champion_mentality.webp',
    '/logos/thupertavage-championmentality.webp',
    '/logos/cookiesandcream-rtd.webp',
    '/logos/Raw_Lightweight_Cap.webp'
  ]

  const productName = apiProduct.Name.toLowerCase()

  // Mapeo inteligente basado en palabras clave
  if (productName.includes('protein') || productName.includes('isolate') || productName.includes('itholate')) {
    if (productName.includes('isolate')) {
      return '/logos/isolateprotein-coldbrew.webp'
    }
    if (productName.includes('itholate')) {
      return '/logos/itholate-championmentality.webp'
    }
    if (productName.includes('rtd')) {
      return '/logos/cookiesandcream-rtd.webp'
    }
    return '/logos/isolateprotein-coldbrew.webp'
  }

  if (productName.includes('creatine') || productName.includes('creatina')) {
    return '/logos/Creatine_30_serv_render.webp'
  }

  if (productName.includes('pre-workout') || productName.includes('preworkout') || productName.includes('thavage')) {
    if (productName.includes('thupertavage') || productName.includes('super')) {
      return '/logos/thupertavage-championmentality.webp'
    }
    if (productName.includes('thavage')) {
      return '/logos/thavage_-_champion_mentality.webp'
    }
    return '/logos/essential_pre-_champion_mentality.webp'
  }

  if (productName.includes('eaa') || productName.includes('amino')) {
    return '/logos/eaaplus-pineapple.webp'
  }

  if (productName.includes('replenish') || productName.includes('hidrat') || productName.includes('hydrat')) {
    return '/logos/REPLENISH_LEMON_LIME_FRONT.webp'
  }

  if (productName.includes('fuel') || productName.includes('energ')) {
    return '/logos/FUEL-Orange-Front_48a9211d-cc14-489a-8ebf-13bf3af02482.webp'
  }

  if (productName.includes('carnitine') || productName.includes('carnitina')) {
    return '/logos/ACETYL_L_CARN_NEW_FRONT_b06488b7-fc9d-4baf-b68e-191985ac0132.webp'
  }

  if (productName.includes('cap') || productName.includes('gorra') || productName.includes('apparel')) {
    return '/logos/Raw_Lightweight_Cap.webp'
  }

  // Si no coincide con nada, usar el ID para asignar una imagen rotativa
  // Si el ID es 0 o inválido, usar la primera imagen por defecto
  const productId = apiProduct.Product_id || 1
  const imageIndex = (productId - 1) % availableImages.length
  return availableImages[imageIndex] || availableImages[0]
}

/**
 * Mapea un producto de la API a un formato compatible con el ecommerce
 */
export function mapApiProductToEcommerce(apiProduct: ApiProduct) {
  // Obtener imagen mockeada o de la API
  const imageUrl = getMockImage(apiProduct)

  // Convertir el precio de string a number
  const price = parseFloat(apiProduct.Price) || 0

  return {
    id: apiProduct.Product_id.toString(),
    name: apiProduct.Name,
    description: apiProduct.Description,
    price: price,
    image: imageUrl,
    stock: apiProduct.Stock,
    sku: apiProduct.Sku,
    category: apiProduct.category?.Name || 'Uncategorized',
    categoryId: apiProduct.Category_id,
    // Campos adicionales para compatibilidad con las vistas
    rating: 4.8, // Por defecto, puede venir de la API más adelante
    reviews: 0, // Por defecto, puede venir de la API más adelante
    badge: 'NEW', // Por defecto
    badgeGradient: 'linear-gradient(135deg, #EDD97A, #c9a94f)',
    longDescription: apiProduct.Description,
    features: [
      'Premium Quality',
      'Fast Shipping',
      'Money Back Guarantee'
    ],
    flavor: undefined as string | undefined,
    servingSize: undefined as string | undefined,
  }
}

/**
 * Mapea un producto de la API a un CartItem
 */
export function mapApiProductToCartItem(apiProduct: ApiProduct): Omit<CartItem, 'quantity'> {
  const imageUrl = getMockImage(apiProduct)

  const price = parseFloat(apiProduct.Price) || 0

  return {
    id: apiProduct.Product_id.toString(),
    name: apiProduct.Name,
    description: apiProduct.Description,
    price: price,
    image: imageUrl
  }
}
